import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import kobukImg from './assets/p1.JPG';

const AlwaysOpenAdventure = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
    const [showFullText, setShowFullText] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const adventure = {
        title: 'What it takes...',
        description:
            'Traveling the trail from Anchorage to Nome by dogsled is tough work — but getting to the starting line has its own set of challenges. There are countless steps involved, and shoot… it’s real expensive too. Signing up for the Iditarod requires several commitments with strict deadlines. The first is the entry fee — $2,000 for the 2026 race. These fees help fund the organization of the race itself, shipping straw to checkpoints, and covering volunteer transportation and logistics that make the event possible. Beyond that, mushers are responsible for shipping their own drop bags. These contain gear, dog food (about 1,700 pounds per team), and human food, all packed according to where they’ll need to resupply along the trail. This usually costs around $2,000. Then there are other logistical costs — shipping sleds, dog crates, and ourselves to and from the finish line in Nome — which typically run around $3,000. But the biggest expense by far is training the dog team. Dog food, gear(for dogs and musher), and travel expenses add up fast. Last winter was especially difficult, with almost no snow at our home kennel. To train, we traveled north 5 separate times in search of good trail. Our favorite spot, the Denali Highway, is about a nine-hour drive from Sterling one way. Each trip meant loading up over 40 dogs, plus trailers packed with food, straw, sleds, gear, snowmachines, and everything else needed to live and train on the road for weeks at a time. It’s rewarding work, but also costly — easily around $5,000 for travel and equipment alone. These are only some of the many more expenses mushers cover to achieve a successful season. It truly takes a whole team of humans and dogs to pull off one musher’s successful Iditarod race. If you’re considering supporting me on this journey, I’d be honored to have you on my team. Any support or donations are deeply appreciated.',
        image: kobukImg,
    };

    const previewLength = Math.floor(adventure.description.length * 0.25);
    const previewText = adventure.description.slice(0, previewLength);

    const styles = {
        container: {
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '100%',
            maxWidth: '3000px',
            margin: isMobile ? '60px auto' : '160px auto 200px',
            backgroundColor: '#D9D9D9',
            borderRadius: '15px',
            overflow: 'hidden',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
        },
        imageContainer: {
            width: isMobile ? '100%' : '50%',
            height: isMobile ? '300px' : 'auto',
            overflow: 'hidden',
            position: 'relative',
        },
        image: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
        },
        textContainer: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: isMobile ? '40px 25px' : '60px 40px',
            textAlign: 'center',
        },
        title: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: isMobile ? '32px' : '42px',
            fontWeight: '700',
            color: '#5FA89B',
            marginBottom: isMobile ? '20px' : '25px',
        },
        description: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: isMobile ? '18px' : '22px',
            color: '#000000',
            lineHeight: 1.6,
            marginBottom: isMobile ? '20px' : '50px',
            whiteSpace: 'pre-line',
            textAlign: 'justify',
        },
        seeToggle: {
            color: '#000',
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'none',
        },
        buttonsRow: {
            display: 'flex',
            gap: '60px',
            justifyContent: 'center',
            flexWrap: 'wrap',
        },
        button: {
            fontFamily: "'Libre Baskerville', serif",
            fontSize: isMobile ? '20px' : '25px',
            fontWeight: 600,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '15px',
            padding: '15px 40px',
            cursor: 'pointer',
            backgroundColor: '#E94560',
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
        },
    };

    return (
        <div style={styles.container}>
            {/* Image on top (mobile) or right (desktop) */}
            {isMobile ? (
                <>
                    <div style={styles.imageContainer}>
                        <motion.img
                            src={adventure.image}
                            alt={adventure.title}
                            style={styles.image}
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6 }}
                        />
                    </div>

                    <div style={styles.textContainer}>
                        <h2 style={styles.title}>{adventure.title}</h2>
                        <p style={styles.description}>
                            {showFullText
                                ? adventure.description
                                : `${previewText}... `}
                            <span
                                style={styles.seeToggle}
                                onClick={() => setShowFullText(!showFullText)}
                            >
                                {showFullText ? ' See less' : ' See more'}
                            </span>
                        </p>

                        <div style={styles.buttonsRow}>
                            <motion.button
                                style={styles.button}
                                whileHover={{
                                    scale: 1.1,
                                    backgroundColor: '#FF5C7A',
                                    boxShadow:
                                        '0 6px 15px rgba(233,69,96,0.6)',
                                }}
                                whileTap={{ scale: 0.95 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 12,
                                }}
                                onClick={() => {
                                    const url = 'https://www.paypal.com/ncp/payment/E68UGTYK4B6KS';
                                    window.location.href = url;
                                }}
                            >
                                DONATE
                            </motion.button>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div style={styles.textContainer}>
                        <h2 style={styles.title}>{adventure.title}</h2>
                        <p style={styles.description}>{adventure.description}</p>

                        <div style={styles.buttonsRow}>
                            <motion.button
                                style={styles.button}
                                whileHover={{
                                    scale: 1.1,
                                    backgroundColor: '#FF5C7A',
                                    boxShadow:
                                        '0 6px 15px rgba(233,69,96,0.6)',
                                }}
                                whileTap={{ scale: 0.95 }}
                                transition={{
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 12,
                                }}
                                onClick={() => {
                                    const url = 'https://www.paypal.com/ncp/payment/E68UGTYK4B6KS';
                                    window.location.href = url;
                                }}
                            >
                                DONATE
                            </motion.button>
                        </div>
                    </div>

                    <div style={styles.imageContainer}>
                        <motion.img
                            src={adventure.image}
                            alt={adventure.title}
                            style={styles.image}
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6 }}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default AlwaysOpenAdventure;
